// /src/pages/BlogIndex.js
import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp } from "lucide-react";
import { getPosts } from "../data/posts";

const TAG_PREVIEW = 8;

export default function BlogIndex() {
  const [posts, setPosts] = useState(null);
  const [err, setErr] = useState("");
  const [activeTag, setActiveTag] = useState(null);
  const [showAllTags, setShowAllTags] = useState(false);

  useEffect(() => {
    let mounted = true;
    getPosts()
      .then((list) => mounted && setPosts(list))
      .catch((e) => {
        console.error(e);
        setErr("Failed to load posts.");
      });
    return () => { mounted = false; };
  }, []);

  const allTags = useMemo(() => {
    if (!posts) return [];
    const counts = {};
    posts.forEach((p) => p.tags.forEach((t) => { counts[t] = (counts[t] || 0) + 1; }));
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([tag]) => tag);
  }, [posts]);

  const visibleTags = showAllTags ? allTags : allTags.slice(0, TAG_PREVIEW);
  const hiddenCount = allTags.length - TAG_PREVIEW;

  const filtered = useMemo(() => {
    if (!posts || !activeTag) return posts;
    return posts.filter((p) => p.tags.includes(activeTag));
  }, [posts, activeTag]);

  const fallback = (msg) => (
    <div className="-mt-12 -mx-4">
      <section className="py-12" style={{ background: 'var(--bg)' }}>
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="font-mono text-base text-[var(--accent)] tracking-wider uppercase mb-4"><span className="opacity-50">$</span> blog</h1>
          <p className="text-[var(--muted)]">{msg}</p>
        </div>
      </section>
    </div>
  );

  if (err) return fallback(err);
  if (!posts) return fallback("Loading posts\u2026");
  if (posts.length === 0) return fallback("No posts found. Check back soon.");

  return (
    <div className="-mt-12 -mx-4">
      {/* Header */}
      <section className="py-12" style={{ background: 'var(--bg)' }}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="max-w-6xl">
            <h1 className="font-mono text-base text-[var(--accent)] tracking-wider uppercase mb-4"><span className="opacity-50">$</span> blog</h1>
            <p className="text-[var(--muted)] mb-6">
              Insights on cybersecurity, software, leadership, and life.
            </p>

            {/* Tag filter - collapsed by default */}
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setActiveTag(null)}
                className="text-xs font-semibold px-3 py-1 rounded-full transition"
                style={{
                  background: !activeTag ? "var(--accent)" : "var(--tag-bg, rgba(255,255,255,0.05))",
                  color: !activeTag ? "#fff" : "var(--tag-text, var(--accent))",
                }}
              >
                All ({posts.length})
              </button>
              {visibleTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => setActiveTag(activeTag === tag ? null : tag)}
                  className="text-xs font-semibold px-3 py-1 rounded-full transition"
                  style={{
                    background: activeTag === tag ? "var(--accent)" : "var(--tag-bg, rgba(255,255,255,0.05))",
                    color: activeTag === tag ? "#fff" : "var(--tag-text, var(--accent))",
                  }}
                >
                  {tag}
                </button>
              ))}
              {hiddenCount > 0 && (
                <button
                  onClick={() => setShowAllTags(!showAllTags)}
                  className="text-xs font-semibold px-3 py-1 rounded-full transition inline-flex items-center gap-1"
                  style={{
                    background: "var(--tag-bg, rgba(255,255,255,0.05))",
                    color: "var(--muted)",
                  }}
                >
                  {showAllTags ? (
                    <>Less <ChevronUp size={12} /></>
                  ) : (
                    <>+{hiddenCount} more <ChevronDown size={12} /></>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      <hr className="section-rule" />

      {/* Post list */}
      <section className="py-10" style={{ background: 'var(--bg2)' }}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="max-w-6xl space-y-0">
            {filtered.map((post, i) => (
              <article
                key={post.slug}
                className={`${i > 0 ? 'border-t border-[color:var(--border)]' : ''}`}
              >
                <div className="blog-card rounded-lg border border-[color:var(--border)] bg-[var(--card)] p-6 my-4">
                  <p className="font-mono text-xs text-[var(--muted)] mb-2">
                    {post.date} {post.readTime && <>&bull; {post.readTime}</>}
                  </p>

                  <h2 className="text-xl font-bold mb-2">
                    <Link
                      to={`/blog/${post.slug}`}
                      className="text-[var(--accent)] hover:opacity-80 hover:underline"
                    >
                      {post.title}
                    </Link>
                  </h2>

                  {post.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {post.tags.map((tag) => (
                        <button
                          key={tag}
                          onClick={() => setActiveTag(activeTag === tag ? null : tag)}
                          className="text-xs font-semibold px-3 py-1 rounded-full transition cursor-pointer"
                          style={{
                            background: activeTag === tag ? "var(--accent)" : "var(--tag-bg, rgba(255,255,255,0.05))",
                            color: activeTag === tag ? "#fff" : "var(--tag-text, var(--accent))",
                          }}
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  )}

                  <p className="text-[var(--text)] mb-4 leading-relaxed text-sm">
                    {post.description || "No description available for this post."}
                  </p>

                  <Link
                    to={`/blog/${post.slug}`}
                    className="text-[var(--accent)] hover:opacity-80 font-semibold inline-flex items-center gap-1 text-sm"
                  >
                    Read Full Post &rarr;
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
